import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CommandRequestBuilderService } from '@app/services/command-request-builder.service';
import { CustomerSupportEvents, TenantEvent, UserEvents } from '@app/services/command-request.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { EntitylementTypes, SnackBarFacade, TenantUtilityTokens, displayMessage } from '@ra-state';
import { ApiEndPoints, CustomerSupportEndpoints, DataService } from '@services/data.service';
import * as R from 'remeda';
import { Observable, combineLatest, map, of, switchMap, throwError } from 'rxjs';
import {
  archiveTenant,
  clearTenantGridRefresh,
  createAdminInvitation,
  getTenantDetails,
  getTenantEffectiveRoles,
  getTenantHierarchyUsers,
  getTenantUsers,
  getTenantUtilityTokens,
  refreshTenantGrid,
  removeUserFromTenant,
  setTenantDetails,
  setTenantEffectiveRoles,
  setTenantHierarchyUsers,
  setTenantUsers,
  setTenantUtilityTokens,
  transferOwnership,
  unarchiveTenant,
} from './tenant.actions';

@Injectable({
  providedIn: 'root',
})
export class TenantEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly snackBarFacade = inject(SnackBarFacade);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);

  getTenantUtilityTokens$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantUtilityTokens),
      switchMap((tenantId) => {
        return this.dataService.getTenantUtilityTokens$(tenantId.value);
      }),
      switchMap((tenantUtilityTokens: TenantUtilityTokens) => {
        // Remove this piece of code when the backend provides the correct combineType
        tenantUtilityTokens.tokenEntitlements.forEach((entitlement) => {
          if (R.isEmpty(entitlement.attributes)) {
            entitlement.attributes = {
              combineType: EntitylementTypes.utilityToken,
            };
          }
        });
        tenantUtilityTokens.disabledTokenEntitlements.forEach((disabledEntitlement) => {
          if (R.isEmpty(disabledEntitlement.attributes)) {
            disabledEntitlement.attributes = {
              combineType: EntitylementTypes.utilityToken,
            };
          }
        });
        return [setTenantUtilityTokens({ payload: tenantUtilityTokens })];
      }),
    );
  });

  getTenantUsers$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantUsers),
      switchMap((payload) => {
        return this.dataService.getTenantUsers$(payload.payload);
      }),
      map((response) => {
        return setTenantUsers({ tenantUsers: response.records });
      }),
    );
  });

  getTenantHierarchyUsers$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantHierarchyUsers),
      switchMap((payload) => {
        return this.dataService.getTenantHierarchyUsers$(payload.payload);
      }),
      map((response) => {
        return setTenantHierarchyUsers({ selectedTenantUsers: response });
      }),
    );
  });

  getTenantDetails$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantDetails),
      switchMap((tenantId) => {
        return this.dataService.getTenantDetail$(tenantId.value);
      }),
      switchMap((tenantDetails) => {
        return [setTenantDetails({ payload: tenantDetails })];
      }),
    );
  });

  transferOwnership$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(transferOwnership),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}owners`, 'PATCH', UserEvents.ResourceOwnershipReassigned)
          .withBody(payload.payload.requestBody)
          .withTenantHeader(payload.payload.tenantId)
          .withWaitOn202Accepted()
          .withTimeout(120)
          .withErrorHandler((error: unknown) => {
            const httpError = error as HttpErrorResponse;
            if (httpError.error) {
              this.snackBarFacade.displayMessage({
                message: 'Please try again.',
                type: 'Error',
                title: 'Ownership Transfer Failed.',
              });
            }
            return throwError(() => error);
          });
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.payload)]);
      }),
      switchMap(([_res, payload]) => {
        const actions: Action[] = [];
        const displayMsg = displayMessage({
          payload: {
            message: payload.message,
            type: 'Success',
          },
        });
        actions.push(
          getTenantDetails({ value: payload.tenantId }),
          getTenantUsers({
            payload: {
              tenantId: payload.tenantId,
            },
          }),
          displayMsg,
        );
        return actions;
      }),
    );
  });

  createAdminInvitation$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(createAdminInvitation),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(CustomerSupportEndpoints.InviteAdmin, 'POST', CustomerSupportEvents.ServiceRequestResolved)
          .withWaitOn200Ok()
          .asAssistedRequest()
          .withTenantHeader(payload.payload.payloadBody.tenantId)
          .withBody(payload.payload.payloadBody);
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.payload)]);
      }),
      map(([_res, _payload]) => {
        return displayMessage({
          payload: {
            message: _payload.message,
            type: 'Success',
          },
        });
      }),
    );
  });

  archiveTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(archiveTenant),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}archived?state=${payload.archive}`, 'PUT', (domainMessage) => {
            return domainMessage.type === TenantEvent.Archived;
          })
          .withTenantHeader(payload.tenantId)
          .withBody({})
          .withWaitOn202Accepted();
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload)]);
      }),
      switchMap(([_domainResponse, payload]) => {
        const actions: Action[] = [];
        actions.push(
          getTenantDetails({ value: payload.tenantId }),
          displayMessage({
            payload: {
              message: `The organization was successfully archived.`,
              type: 'Success',
            },
          }),
        );
        return actions;
      }),
    );
  });

  unarchiveTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(unarchiveTenant),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}archived?state=${payload.archive}`, 'PUT', (domainMessage) => {
            return domainMessage.type === TenantEvent.Unarchived;
          })
          .withTenantHeader(payload.tenantId)
          .withBody({})
          .withWaitOn202Accepted();
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload)]);
      }),
      switchMap(([_domainResponse, payload]) => {
        const actions: Action[] = [];
        actions.push(
          getTenantDetails({ value: payload.tenantId }),
          displayMessage({
            payload: {
              message: `Your organization ${payload.orgName} was successfully restored. It is now available for all current members to use.`,
              type: 'Success',
            },
          }),
        );
        return actions;
      }),
    );
  });

  removeUserFromTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(removeUserFromTenant),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenants}${payload.payload.userId}`, 'DELETE', UserEvents.UserRoleRevoked)
          .withWaitOn200Ok()
          .withTenantHeader(payload.payload.tenantId)
          .withBody({})
          .withErrorMessageMap({
            409: 'An administrator may have archived or modified the organization.',
          });
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.payload)]);
      }),
      switchMap(([_res, _payload]) => {
        return [
          displayMessage({
            payload: {
              message: 'User removed successfully from the organization.',
              type: 'Success',
            },
          }),
          refreshTenantGrid(),
          clearTenantGridRefresh(),
        ];
      }),
    );
  });

  getTenantEffectiveRoles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantEffectiveRoles),
      switchMap((payload) => {
        return this.dataService.getTenantEffectiveRoles$(payload.tenantId, payload.userId);
      }),
      map((response) => {
        return setTenantEffectiveRoles({ tenantEffectiveRoles: response });
      }),
    );
  });
}
